import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { ImageStoreService } from '../image-store/image-store.service';
import {
  map,
  switchMap,
  filter,
  startWith,
  distinctUntilChanged,
  tap,
} from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { ImageState } from '../image-slice-show/share/model';
import { GrayImage3 } from '../image-slice-show/share/image';

export interface ImageCanvasBind {
  image: string;
  canvas: string;
}

export interface ComponentCanvasBind {
  component: string;
  canvas: string;
}

@Injectable(
  // {providedIn: NgThreeModule}
  { providedIn: 'root' },
)
export class MultiCanvasService {
  selectedSubject = new BehaviorSubject<string[]>([]);
  readonly selected$ = this.selectedSubject.asObservable();
  private imageCanvasBindsSubject = new BehaviorSubject<ImageCanvasBind[]>([]);
  readonly imageCanvas$ = this.imageCanvasBindsSubject.asObservable();
  componentCanvasSubject = new BehaviorSubject<ComponentCanvasBind[]>([]);
  componentCanvas$ = this.componentCanvasSubject.asObservable();

  private withCrossLineCanvasSubject = new BehaviorSubject<string[]>([]);
  public withCrossLineCanvas$ = this.withCrossLineCanvasSubject.asObservable();

  readonly selectedImageIds$ = combineLatest(
    this.imageCanvas$,
    this.selected$,
  ).pipe(
    map(([binds, selected]) => {
      return selected
        .map(cid => this.imageIdOf(binds, cid))
        .filter((x): x is string => x !== undefined);
    }),
    distinctUntilChanged(),
  );

  readonly selectedImages$ = this.selectedImageIds$.pipe(
    startWith([] as string[]),
    map(ids => ids.map(id => this.imageStoreService.currentImage(id))),
    distinctUntilChanged(),
  );

  constructor(private imageStoreService: ImageStoreService) {}

  isSelected$(canvas: string): Observable<boolean> {
    return this.selected$.pipe(map(ss => ss.includes(canvas)));
  }

  public isWithCanvas$(c: string) {
    return this.withCrossLineCanvas$.pipe(map(cs => cs.includes(c)));
  }

  addCanvasTo(component: string): string {
    const canvas = uuid();
    this.componentCanvasSubject.next([
      ...this.componentCanvasSubject.value,
      { component, canvas },
    ]);
    return canvas;
  }

  canvasOf(component: string): Observable<string[]> {
    return this.componentCanvas$.pipe(
      map(bs => bs.filter(b => b.component === component)),
      map(bs => bs.map(b => b.canvas)),
      distinctUntilChanged(),
    );
  }

  imageOf$(canvas: string) {
    return this.imageCanvas$.pipe(
      map(bs => bs.find(b => b.canvas === canvas)),
      filter((b): b is ImageCanvasBind => b !== undefined),
      switchMap(b => this.imageStoreService.image$(b.image)),
    );
  }

  viewOf$(canvas: string) {
    return this.imageCanvas$.pipe(
      map(bs => bs.find(b => b.canvas === canvas)),
      filter((b): b is ImageCanvasBind => b !== undefined),
      switchMap(b => this.imageStoreService.image$(b.image)),
      map(img => img.slicer.direction),
    );
  }

  select(canvasId: string) {
    this.selectedSubject.next([canvasId]);
  }

  private imageIdOf(
    binds: ImageCanvasBind[],
    canvasId: string,
  ): string | undefined {
    const bind = binds.find(b => b.canvas === canvasId);
    if (bind === undefined) {
      return undefined;
    }
    return bind.image;
  }

  // addCrossLine() {
  //   this.mapSelectedCanvas((c) => this.addCrossLineToSelected(c));
  // }

  // private addCrossLineToSelected(selectedCanvasId: string) {
  //   const previous = this.withCrossLineCanvasSubject.value;
  //   if (previous.includes(selectedCanvasId)) {
  //     return;
  //   }
  //   this.withCrossLineCanvasSubject.next([...previous, selectedCanvasId]);
  // }

  mapOnCanvas<T>(canvas: string, op: (image: ImageState) => T) {
    const imageId = this.imageIdOf(this.imageCanvasBindsSubject.value, canvas);
    if (imageId === undefined) {
      return;
    }
    const current = this.imageStoreService.currentImage(imageId);
    if (current === undefined) {
      return;
    }
    return op(current);
  }

  loadToCanvas(canvas: string, image: GrayImage3) {
    if (
      this.imageIdOf(this.imageCanvasBindsSubject.value, canvas) !== undefined
    ) {
      console.warn(`Canvas ${canvas} is already occupied, ignore new loads`);
      return;
    }
    this.imageStoreService
      .addImage(image)
      .subscribe(
        id =>
          this.imageCanvasBindsSubject.next([
            ...this.imageCanvasBindsSubject.value,
            { canvas, image: id },
          ]),
        e => console.error(e),
      );
  }


  mapSelectedCanvas<T>(op: (canvas: string) => T) {
    this.selectedSubject.value.forEach(c => op(c));
  }
}
